import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  styled,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  Person,
  ExpandMore,
  ExpandLess,
  LiveTv,
} from "@mui/icons-material";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";

const StyledVideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "16/9",
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  backgroundColor: "#000",
  border: "2px solid transparent",
  transition: "border-color 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const VideoOverlay = styled(Box)({
  position: "absolute",
  top: 8,
  left: 8,
  right: 8,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  zIndex: 2,
});

const StatusChip = styled(Chip)<{ active?: boolean }>(({ theme, active }) => ({
  backgroundColor: active ? "rgba(76, 175, 80, 0.9)" : "rgba(244, 67, 54, 0.9)",
  color: "white",
  fontSize: "0.75rem",
  height: 24,
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: "1px solid rgba(255,255,255,0.12)",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.02)",
  },
}));

interface RemoteUser {
  userId: string;
  userName: string;
  streamID: string;
  stream: MediaStream | null;
  hasVideo: boolean;
  hasAudio: boolean;
}

interface LiveStreamsViewerProps {
  zg?: ZegoExpressEngine | null;
  bookingId?: string;
}

export default function LiveStreamsViewer({ zg, bookingId }: LiveStreamsViewerProps) {
  const [remoteUsers, setRemoteUsers] = useState<Map<string, RemoteUser>>(new Map());
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  // Zego event handlers for remote users
  useEffect(() => {
    if (!zg) return;

    const handleStreamUpdate = async (roomID: string, updateType: string, streamList: any[]) => {
      console.log("Live streams updated:", updateType, streamList);
      
      if (updateType === "ADD") {
        for (const streamInfo of streamList) {
          try {
            const remoteStream = await zg.startPlayingStream(streamInfo.streamID);
            
            setRemoteUsers(prev => {
              const newUsers = new Map(prev);
              newUsers.set(streamInfo.streamID, {
                userId: streamInfo.user.userID,
                userName: streamInfo.user.userName || streamInfo.user.userID,
                streamID: streamInfo.streamID,
                stream: remoteStream,
                hasVideo: streamInfo.hasVideo !== false,
                hasAudio: streamInfo.hasAudio !== false,
              });
              return newUsers;
            });

            // Attach stream to video element
            setTimeout(() => {
              const videoElement = videoRefs.current.get(streamInfo.streamID);
              if (videoElement && remoteStream) {
                videoElement.srcObject = remoteStream;
                videoElement.play().catch(console.error);
              }
            }, 100);
          } catch (error) {
            console.error("Error playing remote stream:", error);
          }
        }
      } else if (updateType === "DELETE") {
        for (const streamInfo of streamList) {
          zg.stopPlayingStream(streamInfo.streamID);
          
          setRemoteUsers(prev => {
            const newUsers = new Map(prev);
            newUsers.delete(streamInfo.streamID);
            return newUsers;
          });

          // Clean up video ref
          videoRefs.current.delete(streamInfo.streamID);
        }
      }
    };

    // Register Zego event listener
    zg.on("roomStreamUpdate", handleStreamUpdate);

    // Cleanup
    return () => {
      zg.off("roomStreamUpdate", handleStreamUpdate);
    };
  }, [zg]);

  // Set video ref when component mounts/updates
  const setVideoRef = (streamID: string) => (ref: HTMLVideoElement | null) => {
    if (ref) {
      videoRefs.current.set(streamID, ref);
      
      // If stream already exists, attach it immediately
      const user = remoteUsers.get(streamID);
      if (user?.stream) {
        setTimeout(() => {
          if (videoRefs.current.get(streamID) === ref) {
            ref.srcObject = user.stream;
            ref.play().catch(console.error);
          }
        }, 50);
      }
    } else {
      videoRefs.current.delete(streamID);
    }
  };

  // Re-attach streams when expanded
  useEffect(() => {
    if (isExpanded && remoteUsers.size > 0) {
      setTimeout(() => {
        remoteUsers.forEach((user, streamID) => {
          const videoElement = videoRefs.current.get(streamID);
          if (videoElement && user.stream) {
            videoElement.srcObject = null;
            videoElement.srcObject = user.stream;
            videoElement.play().catch(console.error);
          }
        });
      }, 100);
    }
  }, [isExpanded, remoteUsers]);

  const remoteUsersList = Array.from(remoteUsers.values());

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#141414",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        color: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <HeaderContainer onClick={() => setIsExpanded(!isExpanded)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LiveTv sx={{ color: "#E50914" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Live Streams
          </Typography>
          <Chip 
            label={`${remoteUsersList.length} active`}
            size="small"
            sx={{
              backgroundColor: "rgba(229,9,20,0.2)",
              color: "#E50914",
              border: "1px solid #E50914",
            }}
          />
        </Box>
        <IconButton size="small" sx={{ color: "#ffffff" }}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </HeaderContainer>

      {/* Streams Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {remoteUsersList.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: "center", 
                py: 4,
                color: "rgba(255,255,255,0.74)"
              }}
            >
              <LiveTv sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">
                No live streams available
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.7 }}>
                Streams will appear here when users join
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {remoteUsersList.map((user) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={remoteUsersList.length === 1 ? 12 : 6}
                  md={remoteUsersList.length <= 2 ? 6 : 4}
                  key={user.streamID}
                >
                  <Card 
                    sx={{ 
                      backgroundColor: "#1c1c1c",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <StyledVideoContainer>
                      {user.hasVideo ? (
                        <video
                          ref={setVideoRef(user.streamID)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          autoPlay
                          playsInline
                          muted
                        />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            backgroundColor: "#000",
                            color: "white",
                          }}
                        >
                          <Avatar 
                            sx={{ 
                              width: 64, 
                              height: 64, 
                              backgroundColor: "#E50914",
                              border: "2px solid rgba(255,255,255,0.2)"
                            }}
                          >
                            <Person sx={{ fontSize: 32 }} />
                          </Avatar>
                        </Box>
                      )}

                      {/* Video Overlay */}
                      <VideoOverlay>
                        <StatusChip
                          icon={user.hasVideo ? <Videocam /> : <VideocamOff />}
                          label={user.hasVideo ? "Video" : "No Video"}
                          active={user.hasVideo}
                          size="small"
                        />
                        <StatusChip
                          icon={user.hasAudio ? <Mic /> : <MicOff />}
                          label={user.hasAudio ? "Audio" : "Muted"}
                          active={user.hasAudio}
                          size="small"
                        />
                      </VideoOverlay>
                    </StyledVideoContainer>

                    <CardContent sx={{ py: 1.5, backgroundColor: "#1c1c1c" }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          color: "#ffffff"
                        }}
                      >
                        {user.userName}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ color: "rgba(255,255,255,0.74)" }}
                      >
                        Stream ID: {user.streamID}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
